import { parseApiError } from './apiError';
import { UsersApi, AuthentificationApi } from '.';

const usersApi = new UsersApi();
const authApi = new AuthentificationApi();

export const getAllUsers = () => usersApi.apiUsersGet().then(res => res.data);
export const getUserById = (id: string, token?: string) => {
  if (token) {
    // Configuration avec token pour l'API
    const config = {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
    return usersApi.apiUsersIdGet(id, config).then(res => res.data);
  }
  return usersApi.apiUsersIdGet(id).then(res => res.data);
};
export const createUser = (data: any) => authApi.apiUsersRegisterPost(data).then(res => res.data);
export const updateUser = (id: string, data: any) => usersApi.apiUsersIdPut(id, data).then(res => res.data);
export const deleteUser = (id: string) => usersApi.apiUsersIdDelete(id).then(res => res.data);

export const getAllUsersWithToken = async (token: string) => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/users`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
};

export const updateUserRole = async (id: string, role: 'user' | 'moderator' | 'admin', token: string) => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/users/${id}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ role })
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
};

// Mise à jour partielle authentifiée — PUT /api/users/:id passe par la
// même route que updateUserRole mais accepte n'importe quel sous-ensemble
// de champs (is_checked, is_suspended, first_name...), grâce au COALESCE
// côté contrôleur.
export const updateUserFields = async (id: string, data: Record<string, any>, token: string) => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/users/${id}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Erreur ${res.status}: ${res.statusText}`);
  }
  return res.json();
};

export const requestPasswordReset = async (email: string) => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/users/request-password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Erreur ${res.status}: ${res.statusText}`);
  }
  return res.json();
};

// Suppression définitive par un admin : tout part (annonces, locations,
// avis, permis, fichiers), rien n'est envoyé à la personne. Résumé de ce
// qui a été retiré en retour, pour le confirmer à l'écran.
export const deleteUserPermanently = async (id: string, token: string): Promise<{ removed?: { vehicles: number; rentals: number; reviews: number; files: number } }> => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/users/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) throw await parseApiError(res, 'Suppression impossible');
  return res.json();
};

export type UserNoShow = {
  id_rental: string;
  start_date: string;
  end_date: string;
  cancellation_code: 'no_show_renter' | 'no_show_owner';
  cancellation_reason: string | null;
  no_show_reported_at: string;
  no_show_contested_at: string | null;
  cancelled_by: string | null;
  vehicle_brand: string | null;
  vehicle_model: string | null;
  absent_role: 'renter' | 'owner';
  reporter_first_name: string | null;
  reporter_last_name: string | null;
};

// Rendez-vous manqués reprochés à un compte (côté locataire ou propriétaire).
export const getUserNoShows = async (id: string, token: string): Promise<{ no_shows: UserNoShow[]; recent_count: number; total: number }> => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/users/${id}/no-shows`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
};
